// App paths the website answers too: the app's in-app sitemap, settings and "about" links land on the web
// equivalent instead of a 404. Plus the company-details, open-source licences and sitemap pages.
import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageMeta } from "@/lib/site";
import { api } from "@/lib/api";

export function Alias({ to }) {
  const { search } = useLocation();
  const params = useParams();
  let dest = to.replace(/:([a-zA-Z]+)/g, (m, k) => params[k] || "");
  if (search && !dest.includes("?")) dest += search;
  return <Navigate to={dest} replace />;
}

export const ALIASES = [
  ["/services", "/worlds/services"], ["/experiences", "/worlds/experiences"], ["/food", "/worlds/food"],
  ["/education", "/worlds/education"], ["/rentals", "/worlds/rentals"], ["/casting", "/worlds/casting"],
  ["/about/blog", "/blog"], ["/about/careers", "/careers"], ["/about/for-businesses", "/business"], ["/about/for-providers", "/partner/start"],
  ["/about/payments", "/payments"], ["/about/pricing", "/pricing"], ["/legal", "/legal/terms"], ["/legal/privacy-policy", "/legal/privacy"],
  ["/become-partner", "/partner/start"], ["/import-business", "/partner/start"], ["/provider/dashboard", "/partner"], ["/provider/plans", "/pricing"],
  ["/join", "/register"], ["/search", "/marketplace"], ["/settings", "/profile"], ["/social-links", "/profile"], ["/edit-profile", "/profile"],
  ["/notification-preferences", "/profile?tab=notifications"], ["/receipts", "/wallet"], ["/invoices", "/wallet"], ["/support", "/contact"],
  ["/connections", "/profile"], ["/feed", "/stage/feed"], ["/privacy", "/profile?tab=privacy"], ["/privacy/:section", "/profile?tab=privacy"],
  ["/wishlist", "/favourites"], ["/trips", "/travel/trips"], ["/trips/:id", "/travel/trips/:id"], ["/tickets/:id", "/tickets"],
  ["/booking-detail/:id", "/bookings"], ["/access/:id", "/collabs?show=backstage"], ["/access/offer/:id", "/collabs?show=backstage"],
  ["/meal-plans", "/worlds/food"], ["/stage/search", "/stage/feed"], ["/live", "/stage/live"], ["/feed/:id", "/stage/post/:id"],
];

function KV({ data }) {
  if (data == null) return null;
  if (Array.isArray(data)) return <ul className="space-y-2">{data.map((x, i) => <li key={i} className="rounded-xl border border-border bg-card p-3 text-sm">{typeof x === "object" ? <KV data={x} /> : String(x)}</li>)}</ul>;
  if (typeof data === "object") return (
    <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-[200px_1fr]">
      {Object.entries(data).filter(([, v]) => v !== null && v !== "" && typeof v !== "boolean").map(([k, v]) => (
        <React.Fragment key={k}><dt className="text-sm font-semibold capitalize text-muted-foreground">{k.replace(/_/g, " ")}</dt><dd className="text-sm">{typeof v === "object" ? <KV data={v} /> : /^https?:/.test(String(v)) ? <a href={v} className="text-primary hover:underline" target="_blank" rel="noopener">{String(v)}</a> : String(v)}</dd></React.Fragment>
      ))}
    </dl>
  );
  return <span>{String(data)}</span>;
}

function DataPage({ title, lede, path, pick }) {
  usePageMeta(title, lede);
  const [d, setD] = useState(undefined);
  useEffect(() => { api(path, { auth: false }).then((r) => setD(pick ? pick(r) : r)).catch(() => setD(null)); }, [path, pick]);
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <p className="text-sm font-semibold text-[#E0007A]">Legal</p>
      <h1 className="mt-1 font-serif-display text-3xl font-bold">{title}</h1>
      <p className="mt-2 text-muted-foreground">{lede}</p>
      <div className="mt-8">{d === undefined ? <Skeleton className="h-48 rounded-2xl" /> : d === null ? <p className="text-muted-foreground">Couldn't load this right now.</p> : <KV data={d} />}</div>
    </div>
  );
}

export function CompanyPage() {
  return <DataPage title="Company details" lede="Who runs Fable + and how to reach us." path="/company-details" />;
}

const pickLicences = (r) => (r && (r.items || r.licences || r.packages)) || r;
export function LicencesPage() {
  return <DataPage title="Open-source licences" lede="Fable + is built on open-source software. Thank you to everyone who made it." path="/legal/licences" pick={pickLicences} />;
}

const SITEMAP = [
  ["Worlds", [["Marketplace", "/marketplace"], ["Events", "/events"], ["Travel", "/travel"], ["Shop", "/shop"], ["Yasmin", "/yasmin"], ["All worlds", "/worlds"]]],
  ["Stage", [["Pulse feed", "/stage/feed"], ["Reels", "/stage/reels"], ["Live", "/stage/live"], ["Creators", "/creators"], ["Circles", "/circles"], ["Collab", "/collabs"], ["Top creators", "/leaderboards/creators"]]],
  ["Plus", [["Membership", "/membership"], ["Verified+", "/plus/verified"], ["Studio", "/studio"], ["Yasmin Plus", "/plus/yasmin"]]],
  ["Your account", [["Profile", "/profile"], ["Bookings", "/bookings"], ["Tickets", "/tickets"], ["Wallet", "/wallet"], ["Messages", "/messages"], ["Notifications", "/notifications"], ["Favourites", "/favourites"], ["Family", "/family"]]],
  ["Business", [["For business", "/business"], ["Pricing", "/pricing"], ["Start selling", "/partner/start"], ["Partner dashboard", "/partner"], ["Payments", "/payments"]]],
  ["Company", [["About", "/about"], ["Blog", "/blog"], ["Careers", "/careers"], ["Contact", "/contact"], ["Get the app", "/get-the-app"]]],
  ["Legal", [["Terms", "/legal/terms"], ["Privacy", "/legal/privacy"], ["Cookies", "/legal/cookies"], ["Affiliate disclosure", "/legal/affiliate-disclosure"], ["Company details", "/legal/company"], ["Licences", "/legal/licences"]]],
];
export function SitemapPage() {
  usePageMeta("Sitemap", "Every page on Fable +.");
  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <h1 className="font-serif-display text-3xl font-bold">Sitemap</h1>
      <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {SITEMAP.map(([h, links]) => (
          <section key={h}><h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{h}</h2><ul className="mt-3 space-y-2">{links.map(([l, to]) => <li key={to}><Link to={to} className="hover:text-[#E0007A] hover:underline">{l}</Link></li>)}</ul></section>
        ))}
      </div>
    </div>
  );
}
